import styled from 'styled-components';

import Button from 'components/shared/Button';
import StandardModal, { ColorThemeName } from 'components/shared/StandardModal';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { ReferralSource } from 'models/ReferralSource';
import { destroyReferralSource } from 'services/api/referralSources';
import { useToastActions } from 'stores/toastStore';
import { BodySmall } from 'styles/typography';

export type DeleteReferralSourceModalProps = {
  referralSource: ReferralSource;
  clientId?: string;
  onClose: () => void;
};

export default function DeleteReferralSourceModal({
  referralSource,
  clientId,
  onClose,
}: DeleteReferralSourceModalProps) {
  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();

  const { mutate, isPending } = useMutateWithToast({ mutationFn: destroyReferralSource });

  const handleDelete = () => {
    mutate(
      { id: referralSource.id, clientId },
      {
        onSuccess: () => {
          invalidateQueries({ referralSources: true });
          addToast({ text: `${referralSource.name} removed from your referral sources` });
          onClose();
        },
      }
    );
  };

  return (
    <StandardModal title='Delete Referral Source?' colorTheme={ColorThemeName.PrimaryRed} onCancel={onClose}>
      <StandardModal.RecordName>{referralSource.name}</StandardModal.RecordName>
      {/* Says what actually happens, which is not a deletion. The last sentence is the part that is
          easy to get wrong: a COMPLETED referral keeps its referral source, because a finished run
          is never re-evaluated, but one still in progress is re-reconciled on its next pass, loses
          the tag and asks again — see ReferralSources::Destroy for the mechanism. Promising that
          everything keeps reporting would have been untrue for exactly the referrals a coordinator
          is still working on. */}
      <Content>
        This referral source will no longer be offered when creating a referral, and new referrals naming it will not be
        matched to it automatically. Referrals you have already completed keep reporting under it. Any referral still in
        progress will ask for its referral source again.
      </Content>
      <StandardModal.Actions>
        <Button variant='ghost' onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant='danger'
          onClick={handleDelete}
          loading={isPending}
          disabled={isPending}
          data-testid='confirm-delete-referral-source'>
          Delete Referral Source
        </Button>
      </StandardModal.Actions>
    </StandardModal>
  );
}

const Content = styled(BodySmall)`
  display: block;
  margin-bottom: 40px;
`;
